import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../../common/axios";
import type { RootStateType } from "../../..";
import { benefitsLogSliceAction } from "../../../slices/partner-program/benefits-log";
import {
  BenefitActivityLogItemType,
  BenefitActivityLogQueryType,
  BenefitActivityLogResponseType,
} from "../../../slices/partner-program/benefits-log/Types";
import {
  GetBenefitsLogPayloadType,
  UpsertBenefitsLogPayloadType,
  DeleteBenefitsLogPayloadType,
  GetBenefitMediaViewUrlPayloadType,
  benefitsLogSagaActionTypes,
  getBenefitsLogSagaAction,
} from "./saga-actions";

type UpsertBenefitsLogResponseType = {
  success: boolean;
  message?: string;
  benefit_log_id?: number;
  files?: Array<{
    media_id: number;
    file_name: string;
    file_path: string;
  }>;
};

type DeleteBenefitsLogResponseType = {
  success: boolean;
  message?: string;
};

const DEFAULT_BENEFITS_LOG_QUERY: BenefitActivityLogQueryType = {
  page: 1,
  limit: 20,
};

const toPositiveNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const buildBenefitsLogQueryParams = (
  partnerId: number,
  query: BenefitActivityLogQueryType
) => {
  const params: Record<string, string | number> = {
    partner_ids: partnerId,
    page: query.page,
    limit: query.limit,
  };
  const search = query.search?.trim();
  if (search) params.search = search;
  if (query.benefitIds?.length) params.benefit_ids = query.benefitIds.join(",");
  if (query.statuses?.length) params.statuses = query.statuses.join(",");
  if (query.hidUserIds?.length) params.hid_user_ids = query.hidUserIds.join(",");
  if (query.partnerUserIds?.length) {
    params.partner_user_ids = query.partnerUserIds.join(",");
  }
  const [plannedFrom, plannedTo] = query.plannedDateRange || [];
  if (plannedFrom) params.planned_date_from = plannedFrom;
  if (plannedTo) params.planned_date_to = plannedTo;
  const [deliveredFrom, deliveredTo] = query.deliveredDateRange || [];
  if (deliveredFrom) params.delivered_date_from = deliveredFrom;
  if (deliveredTo) params.delivered_date_to = deliveredTo;
  return params;
};

const getBenefitsLogAPI = async (
  partnerId: number,
  query: BenefitActivityLogQueryType
) => {
  const response = await axiosRestInstance.get<BenefitActivityLogResponseType>(
    "/partner-program/benefit-activity-log/get",
    { params: buildBenefitsLogQueryParams(partnerId, query) }
  );
  return response.data;
};

const upsertBenefitsLogAPI = async (
  payload: Omit<UpsertBenefitsLogPayloadType, "onSuccess" | "onError">
) => {
  const formData = new FormData();

  if (payload.id) {
    formData.append("id", String(payload.id));
  }
  formData.append("partner_id", String(payload.partner_id));
  if (payload.benefit_id) {
    formData.append("benefit_id", String(payload.benefit_id));
  }
  if (payload.planned_date) {
    formData.append("planned_date", payload.planned_date);
  }
  if (payload.delivered_date) {
    formData.append("delivered_date", payload.delivered_date);
  }
  formData.append("HID_user_id", JSON.stringify(payload.HID_user_id || []));
  formData.append(
    "partner_user_id",
    JSON.stringify(payload.partner_user_id || [])
  );
  formData.append("status", payload.status);
  if (payload.comments !== undefined && payload.comments !== null) {
    formData.append("comments", payload.comments);
  }
  formData.append("created_by", String(payload.created_by));

  (payload.files || []).forEach((file) => {
    formData.append("file_path", file);
  });

  if (payload.deleted_media_ids?.length) {
    formData.append(
      "deleted_media_ids",
      JSON.stringify(payload.deleted_media_ids)
    );
  }

  const response = await axiosRestInstance.post<UpsertBenefitsLogResponseType>(
    "/partner-program/benefit-activity-log/upsert",
    formData
  );
  return response.data;
};

const deleteBenefitsLogAPI = async (id: number) => {
  const response = await axiosRestInstance.delete<DeleteBenefitsLogResponseType>(
    `/partner-program/benefit-activity-log/delete/${id}`
  );
  return response.data;
};

type GetBenefitMediaViewUrlResponseType = {
  success?: boolean;
  data?: {
    url: string;
  };
  url?: string;
  message?: string;
};

const getBenefitMediaViewUrlAPI = async (fileKey: string) => {
  const response = await axiosRestInstance.get<GetBenefitMediaViewUrlResponseType>(
    "/file/url",
    { params: { fileKey, type: "view", source: "benefit_tracker" } }
  );
  return response.data;
};

function* getBenefitsLogSaga(action: {
  type: string;
  payload: GetBenefitsLogPayloadType;
}) {
  const { partnerId, onSuccess, onError } = action.payload;
  const lastQuery: RootStateType["partner_program_benefits_log"]["lastQuery"] =
    yield select(
      (state: RootStateType) => state.partner_program_benefits_log.lastQuery
    );
  const query: BenefitActivityLogQueryType =
    action.payload.query ||
    (lastQuery?.partnerId === partnerId ? lastQuery.query : DEFAULT_BENEFITS_LOG_QUERY);
  try {
    yield put(benefitsLogSliceAction.setBenefitsLogLoading(true));
    yield put(benefitsLogSliceAction.setBenefitsLogLastQuery({ partnerId, query }));
    const response: BenefitActivityLogResponseType = yield call(
      getBenefitsLogAPI,
      partnerId,
      query
    );
    const logs: BenefitActivityLogItemType[] = response?.data || [];
    const pagination = response?.pagination || {};
    yield put(benefitsLogSliceAction.setBenefitsLogList(logs));
    yield put(
      benefitsLogSliceAction.setBenefitsLogPagination({
        currentPage: toPositiveNumber(pagination.current_page) || query.page,
        perPage: toPositiveNumber(pagination.per_page) || query.limit,
        totalRecords: toPositiveNumber(pagination.total_records),
      })
    );
    yield put(benefitsLogSliceAction.setBenefitsLogErrorMessage(""));
    if (onSuccess) {
      onSuccess();
    }
  } catch (e: any) {
    yield put(benefitsLogSliceAction.setBenefitsLogList([]));
    yield put(
      benefitsLogSliceAction.setBenefitsLogPagination({
        currentPage: query.page,
        perPage: query.limit,
        totalRecords: 0,
      })
    );
    const message =
      e?.response?.data?.message ||
      e?.message ||
      "Unable to load benefit activity logs.";
    yield put(benefitsLogSliceAction.setBenefitsLogErrorMessage(message));
    if (onError) {
      onError(message);
    }
  } finally {
    yield put(benefitsLogSliceAction.setBenefitsLogLoading(false));
  }
}

function* upsertBenefitsLogSaga(action: {
  type: string;
  payload: UpsertBenefitsLogPayloadType;
}) {
  const { onSuccess, onError, ...requestPayload } = action.payload;
  try {
    yield put(benefitsLogSliceAction.setBenefitsLogSaving(true));
    const response: UpsertBenefitsLogResponseType = yield call(
      upsertBenefitsLogAPI,
      requestPayload
    );

    if (response?.success === false) {
      if (onError) {
        onError(response?.message || "Unable to save benefit activity log.");
      }
      return;
    }

    yield put(
      getBenefitsLogSagaAction({
        partnerId: requestPayload.partner_id,
      })
    );

    if (onSuccess) {
      onSuccess(
        response?.message ||
          (requestPayload.id
            ? "Benefit activity log updated successfully."
            : "Benefit activity log created successfully.")
      );
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to save benefit activity log."
      );
    }
  } finally {
    yield put(benefitsLogSliceAction.setBenefitsLogSaving(false));
  }
}

function* deleteBenefitsLogSaga(action: {
  type: string;
  payload: DeleteBenefitsLogPayloadType;
}) {
  const { id, partnerId, onSuccess, onError } = action.payload;
  try {
    yield put(benefitsLogSliceAction.setBenefitsLogSaving(true));
    const response: DeleteBenefitsLogResponseType = yield call(
      deleteBenefitsLogAPI,
      id
    );

    if (response?.success === false) {
      if (onError) {
        onError(response?.message || "Unable to delete benefit activity log.");
      }
      return;
    }

    yield put(
      getBenefitsLogSagaAction({
        partnerId,
      })
    );

    if (onSuccess) {
      onSuccess(
        response?.message || "Benefit activity log deleted successfully."
      );
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to delete benefit activity log."
      );
    }
  } finally {
    yield put(benefitsLogSliceAction.setBenefitsLogSaving(false));
  }
}

function* getBenefitMediaViewUrlSaga(action: {
  type: string;
  payload: GetBenefitMediaViewUrlPayloadType;
}) {
  const { fileKey, onSuccess, onError } = action.payload;
  try {
    const response: GetBenefitMediaViewUrlResponseType = yield call(
      getBenefitMediaViewUrlAPI,
      fileKey
    );
    const url = response?.data?.url || response?.url;
    if (!url) {
      if (onError) {
        onError(response?.message || "Unable to open file preview.");
      }
      return;
    }
    if (onSuccess) {
      onSuccess(url);
    }
  } catch (e: any) {
    if (onError) {
      onError(
        e?.response?.data?.message ||
          e?.message ||
          "Unable to open file preview."
      );
    }
  }
}

function* benefitsLogSaga() {
  yield takeLatest(
    benefitsLogSagaActionTypes.callGetBenefitsLogSaga,
    getBenefitsLogSaga
  );
  yield takeLatest(
    benefitsLogSagaActionTypes.callUpsertBenefitsLogSaga,
    upsertBenefitsLogSaga
  );
  yield takeLatest(
    benefitsLogSagaActionTypes.callDeleteBenefitsLogSaga,
    deleteBenefitsLogSaga
  );
  yield takeEvery(
    benefitsLogSagaActionTypes.callGetBenefitMediaViewUrlSaga,
    getBenefitMediaViewUrlSaga
  );
}

export default benefitsLogSaga;
